import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  useResellers, useReseller, useResellerTenants,
  useCreateReseller, useUpdateReseller, useArchiveReseller,
} from '../../lib/api/hooks-resellers.js';
import {
  StatusBadge, TableSkeleton, Empty, ErrorBanner, Stat, StatGroup, Tabs, Tab,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Section, Field, FormActions } from '../../components/ui/Form.js';
import { useSession } from '../../lib/auth/session.js';
import { relativeTime } from '../../lib/format.js';
import { EmailSenderCard } from '../billing/EmailSenderCard.js';
import { BrandEditor } from '../branding/BrandEditor.js';

/**
 * Resellers — the partners who own customers on this platform.
 *
 * The tables have carried `reseller_id` since migration 0003 and no screen ever
 * showed one, so the only way to attach a customer to a partner was to paste a
 * uuid into a request body. This is the screen that makes the field real; the
 * two authorisation holes fixed alongside it were both the same missing check
 * on that pasted id.
 */
export function ResellersPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const can = useSession((s) => s.can);

  const list = useResellers({
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
  });

  if (selected) {
    return <ResellerDetail id={selected} onBack={() => setSelected(null)} />;
  }

  const rows = list.data?.data ?? [];
  const customers = rows.reduce((n, r) => n + r.tenant_count, 0);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Resellers</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Partners who own customers on the platform. A customer filed under a
            reseller is visible to that reseller&rsquo;s staff.
          </p>
        </div>
        {can('reseller:write') && (
          <button type="button" className="arc-btn arc-btn-primary"
            onClick={() => setAdding((v) => !v)}>
            <Icon name={adding ? 'close' : 'plus'} size={13} />
            {adding ? 'Cancel' : 'New reseller'}
          </button>
        )}
      </div>

      {list.error && <ErrorBanner error={list.error} />}
      {adding && <NewResellerForm onDone={() => setAdding(false)} />}

      {rows.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <StatGroup>
            <Stat bare icon="users" hue={1} label="Resellers" value={rows.length}
              sub={`${rows.filter((r) => r.status === 'active').length} active`} />
            <Stat bare icon="accounts" hue={2} label="Customers" value={customers}
              sub="filed under a partner" />
            {/* A reseller with no customers is worth seeing: it is either new,
                or a partner who never onboarded and is still holding a portal
                domain and a set of logins. */}
            <Stat bare icon="warning" hue={3} label="No customers yet"
              value={rows.filter((r) => r.tenant_count === 0).length}
              sub="nothing filed under them" />
          </StatGroup>
        </div>
      )}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Search name or slug…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              aria-label="Search resellers" />
          </div>
          <select className="arc-select" style={{ width: 'auto' }} value={status}
            onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="closed">Closed</option>
          </select>
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {list.data ? `${rows.length} shown` : ''}
          </span>
        </div>

        {list.isLoading ? (
          <TableSkeleton rows={4} cols={5} />
        ) : rows.length === 0 ? (
          <Empty icon="users"
            title={search || status ? 'No reseller matches' : 'No resellers yet'}
            hint={search || status
              ? 'Clear the search or status filter to see the rest.'
              : 'A customer can belong directly to the platform, so this list is allowed to be empty. Add a reseller when a partner starts selling.'} />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <colgroup>
                <col />
                <col style={{ width: '22%' }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 130 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Reseller</th><th>Portal domain</th>
                  <th>Customers</th><th>Staff</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="arc-clickable" onClick={() => setSelected(r.id)}>
                    <td>
                      <div style={{ fontWeight: 550 }}>{r.name}</div>
                      <div className="arc-subtle arc-mono" style={{ marginTop: 2 }}>{r.slug}</div>
                    </td>
                    <td className="arc-muted">
                      {r.portal_domain
                        ? <span className="arc-mono arc-truncate">{r.portal_domain}</span>
                        : <span className="arc-subtle">not white-labelled</span>}
                    </td>
                    <td className="arc-num">
                      {r.tenant_count}
                      {r.active_tenant_count !== r.tenant_count && (
                        <span className="arc-subtle"> · {r.active_tenant_count} open</span>
                      )}
                    </td>
                    <td className="arc-num">{r.user_count}</td>
                    <td><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ------------------------------------------------------------------ detail

function ResellerDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [tab, setTab] = useState<'customers' | 'settings' | 'email' | 'branding'>('customers');
  const can = useSession((s) => s.can);
  const reseller = useReseller(id);
  const r = reseller.data;

  if (reseller.error) return <ErrorBanner error={reseller.error} />;

  return (
    <>
      <button type="button" className="arc-crumb" onClick={onBack}
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <Icon name="arrowLeft" size={13} /> Resellers
      </button>

      <div className="arc-page-head">
        <div>
          <div className="arc-row" style={{ gap: 9 }}>
            <h1 className="arc-h1">{r?.name ?? '…'}</h1>
            {r && r.status !== 'active' && <StatusBadge status={r.status} />}
          </div>
          <p className="arc-mono arc-subtle" style={{ marginTop: 3 }}>
            {r?.portal_domain ?? r?.slug ?? ''}
          </p>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <StatGroup>
          <Stat bare icon="accounts" hue={1} label="Customers"
            value={r ? r.tenant_count : '—'}
            sub={r ? `${r.active_tenant_count} not closed` : undefined} />
          <Stat bare icon="users" hue={2} label="Staff"
            value={r ? r.user_count : '—'} sub="with reseller access" />
          <Stat bare icon="clock" hue={4} label="Partner since"
            value={r ? new Date(r.created_at).toISOString().slice(0, 10) : '—'}
            sub={r ? relativeTime(r.created_at) : undefined} />
        </StatGroup>
      </div>

      <Tabs>
        <Tab active={tab === 'customers'} onSelect={() => setTab('customers')}>Customers</Tab>
        <Tab active={tab === 'settings'} onSelect={() => setTab('settings')}>Settings</Tab>
        {can('billing:read') && (
          <Tab active={tab === 'email'} onSelect={() => setTab('email')}>Email</Tab>
        )}
        {can('branding:write') && (
          <Tab active={tab === 'branding'} onSelect={() => setTab('branding')}>Branding</Tab>
        )}
      </Tabs>

      {tab === 'customers' ? <ResellerCustomers id={id} />
        : tab === 'email' ? <EmailSenderCard resellerId={id} />
        : tab === 'branding' ? <BrandEditor resellerId={id} />
        : r ? <ResellerSettings reseller={r} onArchived={onBack} /> : null}
    </>
  );
}

function ResellerCustomers({ id }: { id: string }) {
  const tenants = useResellerTenants(id);
  const rows = tenants.data?.data ?? [];

  if (tenants.error) return <ErrorBanner error={tenants.error} />;

  return (
    <div className="arc-card">
      {tenants.isLoading ? (
        <TableSkeleton rows={4} cols={4} />
      ) : rows.length === 0 ? (
        <Empty icon="accounts" title="No customers under this reseller"
          hint="Set the reseller on an account when you create it, and it appears here." />
      ) : (
        <div className="arc-table-wrap">
          <table className="arc-table">
            <colgroup>
              <col /><col style={{ width: '26%' }} />
              <col style={{ width: 110 }} /><col style={{ width: 110 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Account</th><th>SIP domain</th>
                <th>Extensions</th><th>Channels</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td>
                    {/* A link, not a row click: this is the same account the
                        console opens everywhere else, so it gets the same URL
                        and support can paste it. */}
                    <Link to="/accounts/$accountId" params={{ accountId: t.slug }}
                      className="arc-stack">
                      <span style={{ fontWeight: 570, color: 'var(--arc-accent)' }}>{t.name}</span>
                      <span className="arc-subtle arc-mono" style={{ fontSize: 12 }}>{t.slug}</span>
                    </Link>
                  </td>
                  <td className="arc-muted arc-mono">
                    <span className="arc-truncate">{t.sip_domain}</span>
                  </td>
                  <td className="arc-num">{t.extension_count}</td>
                  <td className="arc-num">{t.max_concurrent_calls}</td>
                  <td><StatusBadge status={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResellerSettings({ reseller, onArchived }: {
  reseller: import('@arcnet/contracts').Reseller; onArchived: () => void;
}) {
  const can = useSession((s) => s.can);
  const writable = can('reseller:write');
  const update = useUpdateReseller(reseller.id);

  const [name, setName] = useState(reseller.name);
  const [domain, setDomain] = useState(reseller.portal_domain ?? '');
  const [status, setStatus] = useState(reseller.status);

  const dirty = name !== reseller.name
    || domain !== (reseller.portal_domain ?? '')
    || status !== reseller.status;

  const save = () => {
    update.mutate({
      name,
      status,
      portal_domain: domain.trim() === '' ? null : domain.trim(),
    });
  };
  const reset = () => {
    setName(reseller.name);
    setDomain(reseller.portal_domain ?? '');
    setStatus(reseller.status);
  };

  return (
    <>
      <Section title="Identity" description="How this partner is named and reached.">
        <Field label="Name" htmlFor="r-name"
          hint="Shown to their customers wherever branding is applied.">
          <input id="r-name" className="arc-input" value={name} disabled={!writable}
            onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Slug" htmlFor="r-slug"
          hint="Permanent. Product keys are unique per reseller and record it, so renaming would orphan them.">
          <input id="r-slug" className="arc-input arc-mono" value={reseller.slug} disabled />
        </Field>
        <Field label="Portal domain" htmlFor="r-domain" span
          hint="The hostname their customers sign in on. Leave blank to use the platform portal. Unique across the platform.">
          <input id="r-domain" className="arc-input arc-mono" value={domain}
            placeholder="voice.partner.co.za" disabled={!writable}
            onChange={(e) => setDomain(e.target.value)} />
        </Field>
      </Section>

      <Section title="Status" description="Whether this partner is trading."
        columns={1}>
        <Field label="Status" htmlFor="r-status"
          hint="Suspending a reseller does not suspend its customers — their phones keep working. Stopping a customer's service is a separate decision about that customer.">
          <select id="r-status" className="arc-select" value={status} disabled={!writable}
            onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="closed">Closed</option>
          </select>
        </Field>
        {status !== reseller.status && status !== 'active' && reseller.active_tenant_count > 0 && (
          <div className="arc-banner arc-banner-warn" style={{ marginTop: 10 }}>
            <Icon name="warning" size={14} />
            <span>
              {reseller.active_tenant_count} customer
              {reseller.active_tenant_count === 1 ? '' : 's'} still trading under
              this reseller. They are unaffected by this change and keep making
              calls — suspend them individually if that is the intent.
            </span>
          </div>
        )}
      </Section>

      {/*
        Archive sits ABOVE the save bar, which is the opposite of where a danger
        zone usually goes, and it is deliberate.

        FormActions is `position: sticky; bottom: 0` with a z-index. While it is
        stuck it is displaced DOWN from its place in the flow, so it paints over
        whatever follows it -- and it only settles back when the page can be
        scrolled far enough for its natural position to reach the bottom of the
        viewport. This page scrolls 113px, so the card was covered permanently
        rather than merely until you scrolled.

        The rule that falls out: a sticky save bar must be the LAST thing in its
        scroll container. Anything after it is a bet on the page being long
        enough, and short pages lose.
      */}
      {writable && <ArchiveReseller reseller={reseller} onArchived={onArchived} />}

      {writable && (
        <FormActions dirty={dirty} saving={update.isPending}
          onSave={save} onReset={reset} />
      )}
    </>
  );
}

/**
 * Archiving, kept below the save bar and behind a confirmation.
 *
 * The button is only offered when there is nothing attached, because the server
 * refuses otherwise -- offering it and then explaining the refusal is a worse
 * version of the same conversation. When something IS attached, the count is
 * shown instead, which is the thing the operator has to act on first.
 */
function ArchiveReseller({ reseller, onArchived }: {
  reseller: import('@arcnet/contracts').Reseller; onArchived: () => void;
}) {
  const archive = useArchiveReseller();
  const [confirming, setConfirming] = useState(false);
  const blocked = reseller.tenant_count > 0 || reseller.user_count > 0;

  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <div className="arc-card-body">
        <div className="arc-row" style={{ gap: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 550 }}>Archive this reseller</div>
            <div className="arc-subtle" style={{ marginTop: 3 }}>
              {blocked
                ? `Not while ${[
                    reseller.tenant_count > 0
                      ? `${reseller.tenant_count} customer${reseller.tenant_count === 1 ? '' : 's'}`
                      : null,
                    reseller.user_count > 0
                      ? `${reseller.user_count} staff membership${reseller.user_count === 1 ? '' : 's'}`
                      : null,
                  ].filter(Boolean).join(' and ')} remain. Move or close the customers and remove the memberships first.`
                : 'Removes it from every list. Nothing is attached, so nothing else changes. The slug stays taken.'}
            </div>
          </div>
          {!blocked && (
            confirming ? (
              <div className="arc-row" style={{ gap: 8 }}>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => setConfirming(false)}>Cancel</button>
                <button type="button" className="arc-btn arc-btn-danger arc-btn-sm"
                  disabled={archive.isPending}
                  onClick={() => archive.mutate(reseller.id, { onSuccess: onArchived })}>
                  {archive.isPending ? 'Archiving…' : 'Yes, archive'}
                </button>
              </div>
            ) : (
              <button type="button" className="arc-btn arc-btn-danger arc-btn-sm"
                onClick={() => setConfirming(true)}>Archive</button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ create

function NewResellerForm({ onDone }: { onDone: () => void }) {
  const create = useCreateReseller();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [domain, setDomain] = useState('');
  const [touchedSlug, setTouchedSlug] = useState(false);

  /**
   * The slug follows the name until somebody edits it, then it stops. Typing a
   * name and having the slug silently overwrite a deliberate edit is the
   * annoying half of this pattern; the useful half is not typing it twice.
   */
  const setNameAndSlug = (v: string) => {
    setName(v);
    if (!touchedSlug) {
      setSlug(v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40));
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      { name, slug, portal_domain: domain.trim() === '' ? null : domain.trim() },
      { onSuccess: onDone },
    );
  };

  return (
    <form className="arc-card" onSubmit={submit} style={{ marginBottom: 14 }}>
      <div className="arc-card-head"><h2 className="arc-h2">New reseller</h2></div>
      <div className="arc-card-body">
        <div className="arc-grid arc-grid-2" style={{ gap: 14 }}>
          <Field label="Name" htmlFor="n-name" hint="The partner's trading name.">
            <input id="n-name" className="arc-input" required value={name}
              onChange={(e) => setNameAndSlug(e.target.value)} />
          </Field>
          <Field label="Slug" htmlFor="n-slug"
            hint="Lowercase, permanent. Used in URLs and in product keys scoped to this reseller.">
            <input id="n-slug" className="arc-input arc-mono" required value={slug}
              /* The dash is ESCAPED. Browsers compile the pattern attribute
                 with the RegExp `v` flag, under which a bare `-` straight after
                 a range (`0-9`) is a syntax error -- the whole pattern is then
                 discarded and the field silently validates nothing. It reported
                 itself only in the console: "Pattern attribute value
                 [a-z0-9][a-z0-9-]* is not a valid regular expression". */
              pattern="[a-z0-9][a-z0-9\-]*"
              onChange={(e) => { setTouchedSlug(true); setSlug(e.target.value); }} />
          </Field>
          <Field label="Portal domain" htmlFor="n-domain" span
            hint="Optional, and can be added later. Their customers sign in here instead of the platform portal.">
            <input id="n-domain" className="arc-input arc-mono" value={domain}
              placeholder="voice.partner.co.za"
              onChange={(e) => setDomain(e.target.value)} />
          </Field>
        </div>

        <div className="arc-row" style={{ marginTop: 14, justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="arc-btn arc-btn-ghost" onClick={onDone}>Cancel</button>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create reseller'}
          </button>
        </div>
      </div>
    </form>
  );
}
